/**
 * Builds a query string from an object of parameters using a mapping from internal keys to URL parameter names.
 *
 * This utility is useful for building query strings in a reusable way, especially in RTK Query or REST clients,
 * when parameters are optional or need to be mapped to different backend keys.
 *
 * - Ignores `undefined`, `null`, functions, and symbols.
 * - Automatically handles array values by joining them with a comma (e.g. assigned_to=1,2,3).
 * - Converts all values to string using `String(value)`.
 *
 * @example
 * ```ts
 * const params = {
 *   caseId: '123',
 *   includeSummary: true,
 *   tags: ['urgent', 'review'],
 *   page: undefined,
 *   callback: () => {}
 * };
 *
 * const paramsMap = {
 *   caseId: 'case_id',
 *   includeSummary: 'include_summary',
 *   tags: 'tag',
 *   page: 'page',
 *   callback: 'cb'
 * };
 *
 * const queryString = buildQueryString(params, paramsMap);
 * console.log(queryString);
 * /// Output: "case_id=123&include_summary=true&tag=urgent,review"
 * ```
 */

export function buildQueryString<T extends Record<string, unknown>>(
  params: T,
  mapping?: { [K in keyof T]: string }
): string {
  const query = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    const mappedKey = mapping ? mapping[key as keyof T] : key;
    if (!mappedKey) continue;

    if (value !== undefined && value !== null && typeof value !== 'function' && typeof value !== 'symbol') {
      if (typeof value === 'boolean') {
        query.set(mappedKey, value ? 'true' : 'false');
      } else if (Array.isArray(value)) {
        if (value.length > 0) {
          query.set(mappedKey, value.map(String).join(','));
        }
      } else {
        query.set(mappedKey, String(value));
      }
    }
  }

  return query.toString();
}
